import { useCallback, useMemo } from 'react';
import { usePathname } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useEntitlement } from '../providers/SubscriptionProvider.shared';
import { useUpgradeModal } from '../providers/UpgradeModalProvider';
import {
  isPaidCvExportFormat,
  type CvExportFormat,
} from '../lib/cv-export-policy';
import {
  getFeatureLockLabelKey,
  getFeatureLockTier,
} from '../lib/feature-lock-badge';
import { createUpgradeContext } from '../lib/monetization-context';

const EXPORT_FEATURE = 'individual.download_any_format';

export function useCvExportEntitlement() {
  const decision = useEntitlement(EXPORT_FEATURE);
  const upgradeModal = useUpgradeModal();
  const pathname = usePathname();
  const { t } = useTranslation('common');
  const isLoading = decision.reasonCode === 'loading';
  const canDownloadPaidFormats = decision.allowed;
  const upgradeContext = useMemo(
    () =>
      createUpgradeContext({
        decision,
        placement: 'export',
        entryPoint: 'cv.export_format',
        returnTo: pathname,
      }),
    [decision, pathname],
  );
  // The badge on a locked format names the plan that unlocks it (Plus), the
  // same plan the upgrade sheet opens on.
  const lockTier = getFeatureLockTier(EXPORT_FEATURE, decision);
  const lockLabel = t(getFeatureLockLabelKey(lockTier));

  const isFormatLocked = useCallback(
    (format: CvExportFormat | string) =>
      isPaidCvExportFormat(format) && !canDownloadPaidFormats,
    [canDownloadPaidFormats],
  );

  const isFormatLoading = useCallback(
    (format: CvExportFormat | string) =>
      isPaidCvExportFormat(format) && isLoading,
    [isLoading],
  );

  const openUpgrade = useCallback(() => {
    upgradeModal.open(upgradeContext);
  }, [upgradeContext, upgradeModal]);

  return {
    decision,
    canDownloadPaidFormats,
    isFormatLocked,
    isFormatLoading,
    lockTier,
    lockLabel,
    upgradeContext,
    openUpgrade,
  };
}
